// PHASE 1.4 VERIFIER: r1-1a7e2f3c
// Mark Done button removed. Pre-1.4 it called a handler that marked the
// notification's linked job as Completed, which was correct when notifications
// pointed at a follow-up Job but DESTRUCTIVE once Phase 1.4 made notifications
// point at the original flagged job. Rather than risk re-introducing the
// data-loss bug, the button is removed entirely. Completion of follow-ups
// happens via the standard JobFormModal flow (open from kanban/calendar →
// status Completed → completion-note prompt → saved to customer timeline).
import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Wrench, AlertTriangle, MessageSquare, Flag, ChevronRight } from 'lucide-react';

const FLAG_TYPE_CONFIG = {
    callback:  { icon: Phone,         label: 'Callback Needed',  color: 'text-blue-600',   bg: 'bg-blue-50',   border: 'border-blue-200'   },
    followup:  { icon: Wrench,        label: 'Follow-up Job',    color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200' },
    issue:     { icon: AlertTriangle, label: 'Issue to Resolve', color: 'text-red-600',    bg: 'bg-red-50',    border: 'border-red-200'    },
    fyi:       { icon: MessageSquare, label: 'General FYI',      color: 'text-slate-600',  bg: 'bg-slate-50',  border: 'border-slate-200'  },
};

const NotificationsPanel = ({ notifications, onNotificationClick, onClose, onMarkAllRead }) => {
    if (!notifications) return null;

    return (
        <div className="fixed inset-0 z-40" onClick={onClose}>
            <div
                className="absolute top-16 right-5 w-80 max-h-[80vh] overflow-y-auto bg-white rounded-lg shadow-xl border z-50"
                onClick={(e) => e.stopPropagation()}
            >
                {/* HEADER */}
                <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                    <h3 className="font-semibold text-gray-800">Notifications</h3>
                    <button
                        onClick={onMarkAllRead}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    >
                        Mark all read
                    </button>
                </div>

                {/* PHASE 1.6 VERIFIER: r1-16-web-panel-link-16a3f2b8
                    Quick link to the flagged-note triage inbox. Renders only
                    if there's at least one unresolved note_flag notification
                    in the visible list, so users without flagged notes don't
                    see noise. onClick closes the panel after Link triggers
                    the navigation (Link onClick fires before navigation). */}
                {notifications.some(n => n.type === 'note_flag' && !n.resolvedAt) && (
                    <Link
                        to="/flagged"
                        onClick={onClose}
                        className="w-full px-4 py-2 bg-amber-50 hover:bg-amber-100 text-xs font-semibold text-amber-700 flex items-center justify-between border-b border-amber-100 transition-colors"
                    >
                        <span className="flex items-center gap-1.5">
                            <Flag size={11} /> View all flagged for you
                        </span>
                        <ChevronRight size={14} />
                    </Link>
                )}

                {/* LIST */}
                <div className="divide-y">
                    {notifications.length > 0 ? (
                        notifications.map(notification => {
                            const flagConfig = notification.flagType ? FLAG_TYPE_CONFIG[notification.flagType] : null;
                            const FlagIcon = flagConfig?.icon || null;
                            const isResolved = !!notification.resolvedAt;

                            return (
                                <div
                                    key={notification.id}
                                    className={`p-4 transition-colors ${
                                        isResolved ? 'bg-gray-50 opacity-60' :
                                        !notification.isRead ? 'bg-blue-50/50 hover:bg-blue-50' :
                                        'hover:bg-gray-50'
                                    }`}
                                >
                                    {/* Flag type badge */}
                                    {flagConfig && (
                                        <div className={`flex items-center gap-1.5 mb-1.5 px-2 py-0.5 rounded-full border w-fit ${flagConfig.bg} ${flagConfig.border}`}>
                                            <Flag size={9} className={flagConfig.color} />
                                            <FlagIcon size={10} className={flagConfig.color} />
                                            <span className={`text-[10px] font-semibold ${flagConfig.color}`}>{flagConfig.label}</span>
                                            {isResolved && <span className="text-[10px] text-green-600 font-semibold ml-1">· Resolved</span>}
                                        </div>
                                    )}

                                    {/* Message — clickable to navigate */}
                                    <p
                                        onClick={() => onNotificationClick(notification)}
                                        className={`text-sm cursor-pointer ${!notification.isRead && !isResolved ? 'font-bold text-gray-900' : 'text-gray-600'}`}
                                    >
                                        {notification.message}
                                    </p>

                                    <p className="text-xs text-gray-400 mt-1.5">
                                        {notification.createdAt?.toDate ? notification.createdAt.toDate().toLocaleString() : 'Just now'}
                                    </p>
                                </div>
                            );
                        })
                    ) : (
                        <div className="p-8 text-center">
                            <p className="text-sm text-gray-500">No notifications yet.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationsPanel;